import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import apiService from '../services/apiService';
import QueuePreviewMap from './QueuePreviewMap';
import '../styles/QueueView.css';

const QueueView = ({ selectedDate }) => {
  // routeGroups: pedidos agrupados por RUTA (real o predicha), no por ventana horaria.
  // [0] = primera ruta del día (panel izquierdo), resto = panel derecho.
  const [routeGroups, setRouteGroups]     = useState([]);
  const [requiresCall, setRequiresCall]   = useState([]);
  const [removedOrders, setRemovedOrders] = useState([]);
  const [deferredOrders, setDeferredOrders] = useState([]);
  const [loading, setLoading]             = useState(false);
  const [autoRefresh, setAutoRefresh]     = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [viewMode, setViewMode]           = useState('list');
  const [hasRoute, setHasRoute]           = useState(false);

  const loadAllData = useCallback(async () => {
    setLoading(true);
    try {
      const [windowsRes, savedRoutes, predictedRes, sidePanelRes] = await Promise.all([
        apiService.getWindowsStatus(selectedDate),
        apiService.getSavedRoutes(selectedDate).catch(() => []),
        apiService.getPredictedRoutes(selectedDate).catch(() => ({ predictedRoutes: [] })),
        apiService.getRemovedAndDeferred(selectedDate).catch(() => ({ removed: [], deferred: [] }))
      ]);
      const realRoutes = Array.isArray(savedRoutes) ? savedRoutes : [];
      setHasRoute(realRoutes.length > 0);
      setRemovedOrders(sidePanelRes.removed || []);
      setDeferredOrders(sidePanelRes.deferred || []);

      const windows = (windowsRes.windows || [])
        .filter(w => (w.orderCount || 0) > 0 || w.hasRoute);

      if (windows.length === 0) {
        setRouteGroups([]);
        return;
      }

      // Cola completa de todas las ventanas con pedidos — luego se regrupa por ruta.
      const windowQueues = await Promise.all(
        windows.map(async (win) => {
          const start = win.startTime || win.start_time;
          const end   = win.endTime   || win.end_time;
          try {
            const res = await apiService.getQueue(selectedDate, start, end);
            return res.queue || [];
          } catch {
            return [];
          }
        })
      );
      const allItems = windowQueues.flat();

      const toMinutes = (t) => {
        if (!t) return null;
        const p = String(t).split(':');
        return parseInt(p[0], 10) * 60 + parseInt(p[1], 10);
      };

      // purchase_id → nombre de ruta REAL (ya generada, fuente de verdad)
      // realRouteMeta: label → cuándo sale / a qué hora se estima que vuelve el motorizado,
      // para saber si esa ruta ya "terminó" y el panel principal debe avanzar a la siguiente.
      const realRouteByPurchase = new Map();
      const realRouteMeta = new Map();
      realRoutes.forEach((route) => {
        const label = route.name || ('Ruta ' + route.id);
        const dep = toMinutes(route.scheduled_departure);
        const dur = Math.ceil(route.estimated_duration || 0);
        realRouteMeta.set(label, {
          departureTime: route.scheduled_departure,
          estimatedReturnMinutes: dep != null ? dep + dur : null,
        });
        (route.stops || []).forEach((stop) => {
          if (stop.purchase_id != null) realRouteByPurchase.set(stop.purchase_id, label);
        });
      });

      // purchase_id → índice de ruta PREDICHA (aún no generada, estimación con el algoritmo real)
      const predictedRoutes = predictedRes.predictedRoutes || [];
      const predictedRouteByPurchase = new Map();
      predictedRoutes.forEach((r) => {
        (r.orders || []).forEach((o) => predictedRouteByPurchase.set(o.purchase_id, r));
      });

      // Pedidos que predictRoutesForDate conoce (lee ver_purchases) pero que nunca tuvieron fila
      // en delivery_pending_orders_queue, así que no llegaron por getQueue. Sin esto, quedarían
      // completamente invisibles en la cola aunque la ruta real/predicha sí los contemple.
      const coveredIds = new Set(allItems.map(i => i.purchaseId));
      const synthesizeItem = (o, etaOverride) => ({
        purchaseId: o.purchase_id,
        customerName: o.customer_name,
        fullAddress: o.full_address,
        district: o.district,
        latitude: o.latitude,
        longitude: o.longitude,
        urlGoogle: o.url_google,
        deliveryType: o.type === 'DINAMICA' ? 'DYNAMIC' : 'FIXED',
        estimatedDeliveryTime: etaOverride || null,
        windowStart: o.original_window_start,
        windowEnd: o.original_window_end,
        inRoute: false,
        locked: false,
        status: 'PENDING',
        confirmationStatus: null,
        notInQueue: true, // marca para distinguir si se necesita en el futuro
      });
      predictedRoutes.forEach((r) => {
        (r.orders || []).forEach((o) => {
          if (!coveredIds.has(o.purchase_id)) {
            allItems.push(synthesizeItem(o, o.eta));
            coveredIds.add(o.purchase_id);
          }
        });
      });
      (predictedRes.unassignedOrders || []).forEach((o) => {
        if (!coveredIds.has(o.purchase_id)) {
          allItems.push(synthesizeItem(o, null));
          coveredIds.add(o.purchase_id);
        }
      });

      // Agrupar pedidos por ruta en vez de por ventana
      const groupsMap = new Map();
      allItems.forEach((item) => {
        let label, isReal = false, predicted = null, sortKey;
        if (realRouteByPurchase.has(item.purchaseId)) {
          label = realRouteByPurchase.get(item.purchaseId);
          isReal = true;
          sortKey = realRoutes.findIndex(r => (r.name || ('Ruta ' + r.id)) === label);
        } else if (predictedRouteByPurchase.has(item.purchaseId)) {
          predicted = predictedRouteByPurchase.get(item.purchaseId);
          label = 'Ruta ' + predicted.routeIndex + ' (estimada)';
          sortKey = 1000 + predicted.routeIndex;
        } else {
          label = 'Sin asignar';
          sortKey = 9999;
        }
        if (!groupsMap.has(label)) {
          const realMeta = isReal ? realRouteMeta.get(label) : null;
          groupsMap.set(label, {
            label,
            isReal,
            isPredicted: !!predicted,
            departureTime: predicted ? predicted.departureTime : (realMeta ? realMeta.departureTime : null),
            estimatedReturnTime: predicted ? predicted.estimatedReturnTime : null,
            estimatedReturnMinutes: predicted ? predicted.estimatedReturnMinutes : (realMeta ? realMeta.estimatedReturnMinutes : null),
            sortKey,
            items: [],
          });
        }
        groupsMap.get(label).items.push(item);
      });

      const groups = Array.from(groupsMap.values()).sort((a, b) => a.sortKey - b.sortKey);

      // El panel principal debe mostrar la ruta VIGENTE ahora, no siempre la primera del día.
      // Si Ruta 1 ya "terminó" (su retorno estimado ya pasó), avanzar al siguiente grupo
      // (real o predicho) que todavía esté en curso. "Sin asignar" nunca es la vigente.
      const nowMinutes = (() => { const d = new Date(); return d.getHours() * 60 + d.getMinutes(); })();
      let currentIdx = groups.findIndex(
        (g) => g.label !== 'Sin asignar' && (g.estimatedReturnMinutes == null || g.estimatedReturnMinutes > nowMinutes)
      );
      if (currentIdx === -1) {
        const known = groups.filter((g) => g.label !== 'Sin asignar');
        currentIdx = known.length > 0 ? groups.indexOf(known[known.length - 1]) : 0;
      }
      const ordered = currentIdx > 0
        ? [groups[currentIdx], ...groups.slice(0, currentIdx), ...groups.slice(currentIdx + 1)]
        : groups;

      setRouteGroups(ordered);

      // Quitar de "Requieren Llamada" los que ya son visibles en algún grupo
      const visibleIds = new Set(allItems.map(i => i.purchaseId));
      apiService.getRequiresCall(selectedDate)
        .then(res => setRequiresCall((res.orders || []).filter(o => !visibleIds.has(o.purchase_id))))
        .catch(() => {});

    } catch (err) {
      console.error('[QueueView] loadAllData error:', err);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    setRouteGroups([]);
    loadAllData();
  }, [selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => { loadAllData(); }, 30000);
    return () => clearInterval(id);
  }, [autoRefresh, loadAllData]);

  const handleRefresh = () => {
    loadAllData();
    toast.info('Datos actualizados');
  };

  // ── Acciones ────────────────────────────────────────────────
  const handleRemoveFromQueue = (purchaseId) => {
    if (!window.confirm('¿Quitar pedido #' + purchaseId + ' de la cola?')) return;
    setActionLoading(purchaseId + '-remove');
    apiService.removeFromQueue({ purchaseId, windowDate: selectedDate })
      .then(() => { toast.success('Pedido #' + purchaseId + ' removido'); loadAllData(); })
      .catch(() => toast.error('Error al remover pedido'))
      .finally(() => setActionLoading(null));
  };

  const handleCustomerResponse = (purchaseId, response) => {
    const label = response === 'ACCEPTED' ? 'aceptó' : 'rechazó';
    if (!window.confirm('Registrar que el cliente ' + label + ' el cambio (pedido #' + purchaseId + ')?')) return;
    setActionLoading(purchaseId + '-response');
    apiService.handleCustomerResponse({ purchaseId, windowDate: selectedDate, response })
      .then(() => { toast.success('Respuesta registrada'); loadAllData(); })
      .catch(() => toast.error('Error al registrar respuesta'))
      .finally(() => setActionLoading(null));
  };

  const handleProposeFirstRoute = (purchaseId) => {
    if (!window.confirm(
      '¿Simular notificación de cambio a primera ruta para pedido #' + purchaseId + '?\n\n' +
      '(El cliente verá los botones ✔ Aceptó / ✘ Rechazó para simular su respuesta)'
    )) return;
    setActionLoading(purchaseId + '-first');
    apiService.proposeFirstRoute({ purchaseId, windowDate: selectedDate })
      .then(result => {
        if (result.success) toast.success('✅ Notificación simulada — ahora aparecen los botones de respuesta del cliente');
        else toast.warn('No se pudo simular: ' + (result.reason || 'sin motivo'));
        loadAllData();
      })
      .catch(() => toast.error('Error al simular propuesta'))
      .finally(() => setActionLoading(null));
  };

  const handleClearOverflow = (purchaseId) => {
    if (!window.confirm('¿Limpiar la propuesta de cambio de ventana para pedido #' + purchaseId + '?\nEl pedido quedará en su ventana original sin confirmación pendiente.')) return;
    setActionLoading(purchaseId + '-clear');
    apiService.clearOverflowNotification({ purchaseId, windowDate: selectedDate })
      .then(() => { toast.success('Propuesta eliminada. Pedido #' + purchaseId + ' queda en su ventana original.'); loadAllData(); })
      .catch(() => toast.error('Error al limpiar propuesta'))
      .finally(() => setActionLoading(null));
  };

  const handleDeferToNext = (purchaseId, customerName) => {
    if (!window.confirm(`¿Excluir pedido #${purchaseId} (${customerName}) de esta ruta y proponer la siguiente ventana disponible?\nSe notificará al cliente por WhatsApp.`)) return;
    setActionLoading(purchaseId + '-defer');
    apiService.deferToNext({ purchaseId, windowDate: selectedDate })
      .then(() => { toast.success(`Pedido #${purchaseId} diferido. Cliente notificado.`); loadAllData(); })
      .catch(() => toast.error('Error al diferir pedido'))
      .finally(() => setActionLoading(null));
  };

  const handlePackLeft = () => {
    setActionLoading('pack-left');
    apiService.packLeft({ date: selectedDate })
      .then(result => {
        if (result.moved && result.moved.length > 0) {
          toast.success(`Pack-left: ${result.moved.length} pedido(s) movido(s) a ventana más temprana`);
        } else {
          toast.info('Pack-left: todos los DINÁMICAs ya están en la ventana más temprana posible');
        }
        loadAllData();
      })
      .catch(() => toast.error('Error al redistribuir pedidos'))
      .finally(() => setActionLoading(null));
  };

  const handleDeferSilent = (purchaseId, customerName) => {
    if (!window.confirm(`¿Diferir pedido #${purchaseId} (${customerName}) a la siguiente ventana?\nEl pedido se entregará en la próxima ruta. Sin aviso al cliente.`)) return;
    setActionLoading(purchaseId + '-defer-silent');
    apiService.deferSilent({ purchaseId, windowDate: selectedDate })
      .then(result => {
        if (result.success) {
          toast.success(`#${purchaseId} diferido → ${fmt(result.newWindow.start)}–${fmt(result.newWindow.end)}`);
        } else {
          toast.warn('No hay ventana siguiente disponible');
        }
        loadAllData();
      })
      .catch(() => toast.error('Error al diferir pedido'))
      .finally(() => setActionLoading(null));
  };

  const handleMarkNotDelivered = (purchaseId) => {
    const reason = window.prompt('Motivo de no entrega (pedido #' + purchaseId + '):');
    if (!reason || !reason.trim()) return;
    setActionLoading(purchaseId + '-nd');
    apiService.markNotDelivered({ purchaseId, windowDate: selectedDate, reason: reason.trim() })
      .then(() => { toast.success('Pedido marcado como no entregado. Cliente notificado.'); loadAllData(); })
      .catch(() => toast.error('Error al marcar como no entregado'))
      .finally(() => setActionLoading(null));
  };

  // ── Helpers ─────────────────────────────────────────────────
  const fmt = (t) => t ? String(t).substring(0, 5) : '--:--';
  const fmtCreatedAt = (dt) => {
    if (!dt) return null;
    try {
      const d = new Date(dt);
      const date = d.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', timeZone: 'America/Lima' });
      const time = d.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'America/Lima' });
      return `${date} ${time}`;
    } catch { return null; }
  };

  const isWithinDeadline = (deadline) => deadline && new Date() < new Date(deadline);

  const confirmationBadge = (status) => ({
    'PENDING_CUSTOMER_CONFIRMATION': { label: 'Esperando respuesta', cls: 'badge-warning', icon: '⏳' },
    'CONFIRMED_NEXT_WINDOW':         { label: 'Confirmado',          cls: 'badge-success', icon: '✅' },
    'NOT_CONFIRMED_BEFORE_CUTOFF':   { label: 'No confirmó',         cls: 'badge-danger',  icon: '⏰' },
    'REJECTED_BY_CUSTOMER':          { label: 'Rechazado',           cls: 'badge-danger',  icon: '❌' },
  }[status] || null);

  const firstGroup  = routeGroups[0] || null;
  const otherGroups = routeGroups.slice(1);

  // ── Render: ítem en la primera ruta ─────────────────────────
  const renderFirstRouteItem = (item, index) => {
    const cb = item.confirmationStatus ? confirmationBadge(item.confirmationStatus) : null;
    return (
      <div
        key={item.purchaseId}
        className={'queue-item' + (item.priority ? ' priority' : '') + (item.locked ? ' locked' : '') + (item.inRoute ? ' in-route' : '')}
      >
        <div className="queue-item-position">
          <span className="position-number">{item.position || index + 1}</span>
          {item.priority && <span className="priority-badge" title="Prioridad">★</span>}
          {item.locked   && <span className="locked-badge"  title="Bloqueado">🔒</span>}
        </div>

        <div className="queue-item-content">
          <div className="queue-item-header">
            <span className="customer-name">{item.customerName || 'Cliente #' + item.purchaseId}</span>
            {item.windowMoved && item.originalWindowStart && (
              <span className="moved-from-badge" title={`Original: ${fmt(item.originalWindowStart)}–${fmt(item.originalWindowEnd)}`}>
                ⏩ movida de {fmt(item.originalWindowStart)}–{fmt(item.originalWindowEnd)}
              </span>
            )}
            {!item.windowMoved && item.movedFromWindow && (
              <span className="moved-from-badge">⏩ desde {fmt(item.movedFromWindow)}</span>
            )}
          </div>

          <div className="queue-item-details">
            <span className="detail-item"><span className="detail-icon">📍</span>{item.fullAddress || item.address || item.district || 'Sin dirección'}</span>
            <span className="detail-item"><span className="detail-icon">📏</span>{item.distanceFromBase ? parseFloat(item.distanceFromBase).toFixed(2) + ' km' : '--'}</span>
            <span className="detail-item"><span className="detail-icon">⏱️</span>{item.cumulativeTime ? item.cumulativeTime + ' min' : '--'}</span>
            <span className="detail-item"><span className="detail-icon">🕐</span>ETA: {fmt(item.estimatedDeliveryTime)}</span>
            {fmtCreatedAt(item.purchaseCreatedAt) && (
              <span className="detail-item"><span className="detail-icon">🛒</span>Pedido: {fmtCreatedAt(item.purchaseCreatedAt)}</span>
            )}
            {item.windowStart && (
              <span className="detail-item">
                <span className="detail-icon">🪟</span>
                Ventana: {fmt(item.windowStart)}–{fmt(item.windowEnd)}
                {item.windowMoved && item.originalWindowStart && (
                  <span className="original-window-inline"> (original {fmt(item.originalWindowStart)}–{fmt(item.originalWindowEnd)})</span>
                )}
                {!item.windowMoved && !item.locked && item.purchaseWindowStart && item.purchaseWindowStart.substring(0, 5) !== (item.windowStart || '').substring(0, 5) && (
                  <span className="original-window-inline"> (compra {fmt(item.purchaseWindowStart)}–{fmt(item.purchaseWindowEnd)})</span>
                )}
              </span>
            )}
            {(item.latitude && item.longitude) && (
              <span className="detail-item">
                <a
                  href={item.urlGoogle || ('https://www.google.com/maps/search/?api=1&query=' + item.latitude + ',' + item.longitude)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="maps-link"
                >
                  🗺️ Ver en mapa
                </a>
              </span>
            )}
          </div>

          <div className="queue-item-badges">
            {item.inRoute && <span className="badge badge-in-route">🚚 En ruta</span>}
            {item.deliveryType === 'FIXED'
              ? <span className="badge badge-primary">🏠 FIJA</span>
              : item.deliveryType === 'DYNAMIC'
                ? <span className="badge badge-info">🔄 DINÁMICA</span>
                : null
            }
            {cb && <span className={'badge ' + cb.cls}>{cb.icon} {cb.label}</span>}
            {item.customerNotified && !cb && <span className="badge badge-info">📱 Notificado</span>}
          </div>
        </div>

        <div className="queue-item-actions">
          <span className="purchase-id">#{item.purchaseId}</span>

          {!item.inRoute && item.confirmationStatus === 'PENDING_CUSTOMER_CONFIRMATION' && (
            isWithinDeadline(item.responseDeadline) ? (
              <div className="action-group">
                <button className="btn btn-sm btn-success"
                  disabled={actionLoading === item.purchaseId + '-response'}
                  onClick={() => handleCustomerResponse(item.purchaseId, 'ACCEPTED')}>✔ Aceptó</button>
                <button className="btn btn-sm btn-danger"
                  disabled={actionLoading === item.purchaseId + '-response'}
                  onClick={() => handleCustomerResponse(item.purchaseId, 'REJECTED')}>✘ Rechazó</button>
              </div>
            ) : (
              <span className="deadline-expired">⏰ Tiempo expirado</span>
            )
          )}

          {!item.inRoute && item.confirmationStatus === 'PENDING_CUSTOMER_CONFIRMATION' && !hasRoute && (
            <button className="btn btn-sm btn-outline-warning"
              disabled={actionLoading === item.purchaseId + '-clear'}
              onClick={() => handleClearOverflow(item.purchaseId)}
              title="Eliminar propuesta de cambio de ventana incorrecta">🧹 Limpiar propuesta</button>
          )}

          {!item.inRoute && item.deliveryType === 'FIXED' && !item.confirmationStatus && !item.locked && !hasRoute && (
            <button className="btn btn-sm btn-outline-secondary"
              disabled={actionLoading === item.purchaseId + '-defer'}
              onClick={() => handleDeferToNext(item.purchaseId, item.customerName)}
              title="Excluir de esta ruta y proponer siguiente ventana al cliente">⏭ Siguiente ruta</button>
          )}

          {!item.inRoute && !item.locked && !item.confirmationStatus && (
            <button className="btn btn-sm btn-outline-warning"
              disabled={actionLoading === item.purchaseId + '-defer-silent'}
              onClick={() => handleDeferSilent(item.purchaseId, item.customerName)}
              title="Diferir a siguiente ruta sin avisar al cliente">⏩ Diferir</button>
          )}

          {!item.inRoute && item.status !== 'REMOVED' && item.deliveryStatus !== 'DELIVERED' && item.deliveryStatus !== 'NOT_DELIVERED' && (
            <button className="btn btn-sm btn-outline-danger"
              disabled={actionLoading === item.purchaseId + '-nd'}
              onClick={() => handleMarkNotDelivered(item.purchaseId)}
              title="Marcar como no entregado">❌ No entregado</button>
          )}

          {!item.inRoute && !item.locked && item.status !== 'REMOVED' && (
            <button className="btn btn-sm btn-link text-danger"
              disabled={actionLoading === item.purchaseId + '-remove'}
              onClick={() => handleRemoveFromQueue(item.purchaseId)}
              title="Remover del día (no se entregará)">🗑 Del día</button>
          )}
        </div>
      </div>
    );
  };

  // ── Render: ítem en otras ventanas ──────────────────────────
  const renderOtherWindowItem = (item) => {
    const canPropose = !item.locked && item.status === 'PENDING' && !item.confirmationStatus;
    const cb = item.confirmationStatus ? confirmationBadge(item.confirmationStatus) : null;

    return (
      <div key={item.purchaseId} className="other-order-card">
        <div className="other-order-main">
          <div className="other-order-name">
            {item.customerName || 'Cliente #' + item.purchaseId}
            {item.windowMoved && item.originalWindowStart && (
              <span className="moved-from-badge" title={`Original: ${fmt(item.originalWindowStart)}–${fmt(item.originalWindowEnd)}`}>
                ⏩ movida de {fmt(item.originalWindowStart)}–{fmt(item.originalWindowEnd)}
              </span>
            )}
          </div>
          <div className="other-order-details">
            <span>📍 {item.fullAddress || item.address || item.district || 'Sin dirección'}</span>
            <span>📏 {item.distanceFromBase ? parseFloat(item.distanceFromBase).toFixed(2) + ' km' : '--'}</span>
            <span>🕐 ETA {fmt(item.estimatedDeliveryTime)}</span>
            {!item.inRoute && item.windowStart && (
              <span className="other-order-window">🪟 Ventana: {fmt(item.windowStart)}–{fmt(item.windowEnd)}</span>
            )}
            {fmtCreatedAt(item.purchaseCreatedAt) && <span>🛒 Pedido: {fmtCreatedAt(item.purchaseCreatedAt)}</span>}
            {(item.latitude && item.longitude) && (
              <a
                href={item.urlGoogle || ('https://www.google.com/maps/search/?api=1&query=' + item.latitude + ',' + item.longitude)}
                target="_blank"
                rel="noopener noreferrer"
                className="maps-link"
              >
                🗺️ Ver en mapa
              </a>
            )}
          </div>
          <div className="other-order-badges">
            {item.inRoute && <span className="badge badge-in-route">🚚 En ruta</span>}
            {item.deliveryType === 'FIXED'
              ? <span className="badge badge-primary">🏠 FIJA</span>
              : item.deliveryType === 'DYNAMIC'
                ? <span className="badge badge-info">🔄 DINÁMICA</span>
                : null
            }
            {cb && <span className={'badge ' + cb.cls}>{cb.icon} {cb.label}</span>}
          </div>
        </div>
        <div className="other-order-actions">
          {!item.inRoute && (
            item.confirmationStatus === 'PENDING_CUSTOMER_CONFIRMATION' ? (
              <>
                {isWithinDeadline(item.responseDeadline) ? (
                  <div className="action-group">
                    <button className="btn btn-sm btn-success"
                      disabled={actionLoading === item.purchaseId + '-response'}
                      onClick={() => handleCustomerResponse(item.purchaseId, 'ACCEPTED')}>✔ Aceptó</button>
                    <button className="btn btn-sm btn-danger"
                      disabled={actionLoading === item.purchaseId + '-response'}
                      onClick={() => handleCustomerResponse(item.purchaseId, 'REJECTED')}>✘ Rechazó</button>
                  </div>
                ) : (
                  <span className="deadline-expired">⏰ Expirado</span>
                )}
                {!hasRoute && (
                  <button className="btn btn-sm btn-outline-warning"
                    disabled={actionLoading === item.purchaseId + '-clear'}
                    onClick={() => handleClearOverflow(item.purchaseId)}
                    title="Eliminar propuesta de cambio de ventana incorrecta">🧹 Limpiar propuesta</button>
                )}
              </>
            ) : canPropose ? (
              <button className="btn btn-sm btn-propose"
                disabled={actionLoading === item.purchaseId + '-first'}
                onClick={() => handleProposeFirstRoute(item.purchaseId)}
                title="Simular notificación al cliente para mover a la 1ª ruta">⚡ Notificar</button>
            ) : null
          )}
          <span className="purchase-id">#{item.purchaseId}</span>
        </div>
      </div>
    );
  };

  // ── Render: requieren llamada ────────────────────────────────
  const renderCallItem = (item) => (
    <div key={item.purchase_id} className="requires-call-item">
      <div className="call-item-header">
        <span className="customer-name">{item.customer ? item.customer.name : 'Cliente #' + item.purchase_id}</span>
        <span className="call-icon">📞</span>
      </div>
      <div className="call-item-details">
        {item.customer && item.customer.phone && (
          <a href={'tel:' + item.customer.phone} className="phone-link">{item.customer.phone}</a>
        )}
        <span className="window-time">{fmt(item.window_start_time)} - {fmt(item.window_end_time)}</span>
      </div>
      {item.notes && <div className="call-item-notes">{item.notes}</div>}
    </div>
  );

  // ── JSX ─────────────────────────────────────────────────────
  return (
    <div className="queue-view">

      {/* Header */}
      <div className="queue-header">
        <div className="header-left">
          <h2>Primera Ruta del Día</h2>
          <span className="date-display">{selectedDate}</span>
        </div>
        <div className="header-right">
          <label className="auto-refresh-toggle">
            <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
            Auto-refresh (30s)
          </label>
          <button className="btn btn-primary" onClick={handleRefresh} disabled={loading}>
            {loading ? '⏳' : '🔄'} Actualizar
          </button>
        </div>
      </div>

      {/* Sin rutas ni pedidos */}
      {!firstGroup && !loading && (
        <div className="empty-state">
          <span className="empty-icon">📭</span>
          <p>No hay pedidos para <strong>{selectedDate}</strong></p>
        </div>
      )}

      {loading && !firstGroup && (
        <div className="empty-state"><p>⏳ Cargando...</p></div>
      )}

      {firstGroup && (
        <div className="queue-split">

          {/* ── Zona izquierda: Primera Ruta ── */}
          <div className="first-route-panel">
            <div className="panel-header">
              <div className="panel-title">
                <h3>{firstGroup.label}</h3>
                <span className="order-count">{firstGroup.items.length} pedido{firstGroup.items.length !== 1 ? 's' : ''}</span>
                {firstGroup.isReal && (
                  <span className="route-generated-badge" title="Ruta ya generada">✅ Ruta generada</span>
                )}
                {firstGroup.isPredicted && (
                  <span className="route-generated-badge-sm" title="Estimación con el algoritmo real — aún no se generó">🔮 Estimada{firstGroup.departureTime ? ' · sale ' + fmt(firstGroup.departureTime) : ''}</span>
                )}
              </div>
              <div className="panel-controls">
                <div className="view-toggle">
                  <button className={'toggle-btn' + (viewMode === 'list' ? ' active' : '')} onClick={() => setViewMode('list')}>☰ Lista</button>
                  <button className={'toggle-btn' + (viewMode === 'map'  ? ' active' : '')} onClick={() => setViewMode('map')}>🗺️ Mapa</button>
                  <button
                    className="toggle-btn"
                    onClick={handlePackLeft}
                    disabled={actionLoading === 'pack-left'}
                    title="Reorganizar pedidos a la ruta más próxima disponible"
                  >
                    {actionLoading === 'pack-left' ? '⏳' : '⏩'} Pack
                  </button>
                </div>
              </div>
            </div>

            {viewMode === 'map' ? (
              <QueuePreviewMap
                queue={firstGroup.items}
                windowLabel={firstGroup.label}
                windowStart={firstGroup.departureTime || null}
              />
            ) : firstGroup.items.length === 0 ? (
              <div className="empty-state">
                <span className="empty-icon">📭</span>
                <p>Aún no hay pedidos en esta ruta</p>
              </div>
            ) : (
              <div className="queue-list">
                {firstGroup.items.map(renderFirstRouteItem)}
              </div>
            )}

            {/* Requieren llamada */}
            {requiresCall.length > 0 && (
              <div className="requires-call-section">
                <h3>📞 Requieren Llamada ({requiresCall.length})</h3>
                <div className="requires-call-list">
                  {requiresCall.map(renderCallItem)}
                </div>
              </div>
            )}
          </div>

          {/* ── Zona derecha: demás rutas + removidos + diferidos ── */}
          <div className="other-windows-panel">

            {/* Pedidos en otras rutas */}
            <h3 className="other-windows-title">Pedidos en otras rutas</h3>
            {otherGroups.length === 0 ? (
              <div className="empty-state" style={{ padding: '20px 16px' }}>
                <p>No hay pedidos en otras rutas</p>
              </div>
            ) : (
              otherGroups.map((group) => (
                <div key={group.label} className="other-window-group">
                  <div className="other-window-title">
                    <span className="other-window-time">{group.label}</span>
                    <span className="other-window-count">{group.items.length} pedido{group.items.length !== 1 ? 's' : ''}</span>
                    {group.isReal && (
                      <span className="route-generated-badge-sm" title="Ruta ya generada">✅ Ruta</span>
                    )}
                    {group.isPredicted && (
                      <span className="route-generated-badge-sm" title="Estimación con el algoritmo real — aún no se generó">🔮 Estimada{group.departureTime ? ' · sale ' + fmt(group.departureTime) : ''}</span>
                    )}
                  </div>
                  <div className="other-orders-list">
                    {group.items.map(renderOtherWindowItem)}
                  </div>
                </div>
              ))
            )}

            {/* Diferidos a siguiente ruta */}
            {deferredOrders.length > 0 && (
              <div className="side-section">
                <h3 className="other-windows-title">⏩ Diferidos a siguiente ruta ({deferredOrders.length})</h3>
                {deferredOrders.map(o => (
                  <div key={o.purchase_id} className="side-order-card side-deferred">
                    <div className="side-order-name">{o.customer_name || '#' + o.purchase_id} <span className="purchase-id">#{o.purchase_id}</span></div>
                    <div className="side-order-meta">
                      <span>🪟 {fmt(o.moved_from_window)} → {fmt(o.window_start_time)}</span>
                      <span>📍 {o.district || o.address || '—'}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Removidos del día */}
            {removedOrders.length > 0 && (
              <div className="side-section">
                <h3 className="other-windows-title">🗑 Removidos del día ({removedOrders.length})</h3>
                {removedOrders.map(o => (
                  <div key={o.purchase_id} className="side-order-card side-removed">
                    <div className="side-order-name">{o.customer_name || '#' + o.purchase_id} <span className="purchase-id">#{o.purchase_id}</span></div>
                    <div className="side-order-meta">
                      <span>🪟 {fmt(o.window_start_time)}–{fmt(o.window_end_time)}</span>
                      <span>📍 {o.district || o.address || '—'}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

          </div>

        </div>
      )}
    </div>
  );
};

export default QueueView;
